import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';

interface TabItem {
  label: string;
  value: string;
  content: React.ReactNode;
}

interface CustomTabsProps {
  tabs: TabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  hasContentBox?: boolean;
  constrainWidth?: boolean;
  fullWidthContent?: boolean;
  rightContent?: React.ReactNode;
}

function CustomTabs({
  tabs,
  defaultValue,
  value,
  onValueChange,
  hasContentBox = false,
  constrainWidth = true,
  fullWidthContent = false,
  rightContent
}: Readonly<CustomTabsProps>): React.ReactElement {
  const tabValue = value ?? defaultValue ?? tabs[0]?.value ?? '';
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [indicatorLeft, setIndicatorLeft] = useState(0);
  const [indicatorWidth, setIndicatorWidth] = useState(0);

  const currentIndex = tabs.findIndex((tab) => tab.value === tabValue);
  const isFirstTab = currentIndex === 0;
  const isLastTab = currentIndex === tabs.length - 1;
  const showTabStrip = tabs.length > 1;

  useLayoutEffect(() => {
    const activeTab = tabsListRef.current?.querySelector(
      '[data-state="active"]'
    ) as HTMLElement;
    if (activeTab && tabsListRef.current) {
      const { left: activeLeft } = activeTab.getBoundingClientRect();
      const { left: containerLeft } =
        tabsListRef.current.getBoundingClientRect();
      setIndicatorLeft(activeLeft - containerLeft);
      setIndicatorWidth(activeTab.offsetWidth);
    }
  }, [tabValue]);

  useEffect(() => {
    const handleResize = (): void => {
      const activeTab = tabsListRef.current?.querySelector(
        '[data-state="active"]'
      ) as HTMLElement;
      if (activeTab && tabsListRef.current) {
        const { left: activeLeft } = activeTab.getBoundingClientRect();
        const { left: containerLeft } =
          tabsListRef.current.getBoundingClientRect();
        setIndicatorLeft(activeLeft - containerLeft);
        setIndicatorWidth(activeTab.offsetWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navigate = (direction: 'left' | 'right'): void => {
    if (direction === 'left' && !isFirstTab) {
      onValueChange?.(tabs[currentIndex - 1].value);
    } else if (direction === 'right' && !isLastTab) {
      onValueChange?.(tabs[currentIndex + 1].value);
    }
  };

  return (
    <Tabs value={tabValue} onValueChange={onValueChange}>
      <div className='flex justify-between items-start md:flex-row flex-col md:gap-0 gap-2'>
        <div
          className={`bg-tabs-bg gap-0 w-full p-1 rounded-md items-center ${
            showTabStrip ? 'flex' : 'hidden'
          } ${constrainWidth ? 'sm:max-w-[700px]' : 'sm:w-fit'}`}
        >
          <button
            type='button'
            onClick={() => navigate('left')}
            className={`flex sm:hidden flex-shrink-0 p-2 text-gray-600 ${
              isFirstTab ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
            }`}
            disabled={isFirstTab}
            aria-label='Previous tab'
          >
            <ChevronLeft size={20} />
          </button>

          <div ref={tabsListRef} className='flex-1 flex'>
            <TabsList className='relative bg-transparent gap-0 w-full p-0 rounded-md flex'>
              <div
                style={{
                  position: 'absolute',
                  left: `${indicatorLeft}px`,
                  width: `${indicatorWidth}px`,
                  height: '100%',
                  backgroundColor: 'white',
                  borderRadius: 'inherit',
                  pointerEvents: 'none',
                  transition: 'none',
                  zIndex: 0
                }}
              />
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className={`
                  font-normal rounded-md px-2 py-1.5 whitespace-nowrap relative z-10 transition-none
                  hover:bg-white data-[state=active]:text-black
                  data-[state=active]:flex data-[state=inactive]:hidden
                  sm:data-[state=inactive]:flex sm:data-[state=active]:flex
                  data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-500
                  flex-1
                `}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <button
            type='button'
            onClick={() => navigate('right')}
            className={`flex sm:hidden flex-shrink-0 p-2 text-gray-600 ${
              isLastTab ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
            }`}
            disabled={isLastTab}
            aria-label='Next tab'
          >
            <ChevronRight size={20} />
          </button>
        </div>
        {rightContent}
      </div>
      {tabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value}>
          {hasContentBox ? (
            <div
              className={`mt-2 px-4 pt-4 pb-4 flex flex-col max-w-full ${
                fullWidthContent ? 'w-full' : 'w-full sm:w-fit'
              } border border-tabs-bg rounded-md`}
            >
              {tab.content}
            </div>
          ) : (
            <div className='mt-3'>{tab.content}</div>
          )}
        </TabsContent>
      ))}
    </Tabs>
  );
}

export default CustomTabs;
